/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Anchor } from 'antd';
import {
  debounce,
  filter,
  findIndex,
  isEmpty,
  isString,
  map,
  split,
} from 'lodash';
import process from 'process';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { trackInPageNavigation } from '../../../common/analytics/analyticsTracker';
import { getArticleTypeInGA } from '../../../common/analytics/helpers/articleTypeHelper';
import { ADP } from '../../../common/analytics/sourceTypes';
import { AdUnits } from '../../../common/helpers/ads/dfp';
import { MAX_CONTENT_HEIGHT } from '../../../common/helpers/constants';
import {
  anchorContentTitle,
  anchorContentTitleEnglish,
  postCategory as articlePostCategory,
} from '../../../common/helpers/domain/postCategories';
import { VideoType } from '../../../common/helpers/domain/videoTypes';
import logger from '../../../common/helpers/logger/logger';
import { handleImageUrl } from '../../../common/helpers/sharpie/imageUrlHelper';
import { splitContentByParagraph } from '../../../common/helpers/ui/dataFilter';
import {
  isMobile,
  isTablet,
} from '../../../common/helpers/utility/screenHelper';
import { getTextBetween } from '../../../common/helpers/utility/textManipulation';
import { LatestProjectCard } from '../../../components/latestProjectReviewCard/LatestProjectReviewCard';
import contentResource from '../../../locales/th/content.json';
import {
  ContentAnchor,
  Post,
  PostGallery,
} from '../../../queries/contentPage/postDetail';
import { WholeState } from '../../../store/common/type';
import CardsAds from '../../ads/CardsAds';
import LazyImage from '../../lazyImage/LazyImage';
import MobileCarousel from '../../mobileCarousel/MobileCarousel';
import ShowMore from '../../showMore/ShowMore';
import ContentCarousel from './ContentCarousel';
import {
  ContentHeroImageWrapper,
  DotBackground,
  Image,
  ItemWrapper,
  PostArticle,
  PostContentContainer,
  PostContentWrapper,
  ShowMoreWrapper,
  FormContent,
} from './PostContent.style';
import { contentHeroImageConfig, imageConfig } from './config';
import { LeadsCapture } from '../../leadsCapture/LeadsCapture';

const PARAGRAPH_REGULAR = /^<p>([\s\S]*)<\/p>$/im;
const PARAGRAPH_WITH_ATTR_REGULAR = /^<p\s*.*?>([\s\S]*)<\/p>$/im;
const FIRST_AD_PARAGRAPH_INTERVAL = 3;
const REST_AD_PARAGRAPH_INTERVAL = 4;
const MAX_TABLET_AD_NUMBER = 2;
const MAX_MOBILE_AD_NUMBER = 3;
const ELIGIBLE_LAZY_LOAD_IMAGE =
  /<img.*?src="(.*?)" alt="(.*?)" width="(.*?)" height="(.*?)"[^>]+>(?:<span[^>]+>(.*)<\/span>)?<\/p>/g;

const PostContent: React.FC<PostContentProps> = ({
  postingData,
  sect,
  subSect,
  onHeightChange,
  showLeadCaptureForm,
}) => {
  const timeWarningLogged = useRef(false);
  const startTime = new Date().getTime();
  const [isShowMore, setIsShowMore] = useState(true);
  const [morePostLoaded, setMorePostLoaded] = useState(false);
  const [inPageNavSectionHeight, setInPageNavSectionHeight] = useState<
    number[]
  >([]);
  const [heroImageHeight, setHeroImageHeight] = useState(0);
  const [isLeftArrowActive, setIsLeftArrowActive] = useState(false);
  const [isRightArrowActive, setIsRightArrowActive] = useState(false);

  const deviceType = useSelector((state: WholeState) =>
    state.common !== undefined ? state.common.deviceType : 'desktop',
  );
  const isNextGenSupported = useSelector<WholeState, boolean>((state) =>
    state.common !== undefined ? state.common.isNextGenSupported : false,
  );
  const imageInfo: { width: number; height: number } = imageConfig[deviceType];
  const userInfo = useSelector((state: WholeState) =>
    state !== undefined ? state.common.user : {},
  );
  const anchorlinkRef = useRef(null);

  //helper functions
  function canScrollToTheLeft(element: HTMLElement) {
    return element.scrollLeft > 0;
  }
  function canScrollToTheRight(element: HTMLElement) {
    const maxScrollValue = element.scrollWidth - element.clientWidth;
    return element.scrollLeft < maxScrollValue - 2; //2 is the threshold value to check if the element is scrolled to the end or not
  }
  //function to handle navigation arrows
  function handleNavigationArrows(
    element: HTMLElement = anchorlinkRef.current,
  ) {
    setIsLeftArrowActive(canScrollToTheLeft(element));
    setIsRightArrowActive(canScrollToTheRight(element));
  }

  useEffect(() => {
    const updateOverflowStatus = () => {
      if (anchorlinkRef.current) {
        setIsRightArrowActive(canScrollToTheRight(anchorlinkRef.current));
      }
    };

    const handleResize = () => {
      requestAnimationFrame(updateOverflowStatus);
    };

    // Initial check for overflow
    if (anchorlinkRef && anchorlinkRef.current) {
      requestAnimationFrame(updateOverflowStatus);
      // Check overflow whenever the window is resized
      window.addEventListener('resize', handleResize);

      // MutationObserver to detect changes to the body of the document
      const observer = new MutationObserver(() => {
        requestAnimationFrame(updateOverflowStatus);
      });

      observer.observe(anchorlinkRef.current, {
        childList: true,
        subtree: true,
      });
      // Clean up the event listener and the MutationObserver when the component is unmounted
      return () => {
        window.removeEventListener('resize', handleResize);
        observer.disconnect();
      };
    }
  }, []);

  const handleScroll = useCallback(() => {
    handleNavigationArrows(anchorlinkRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const element = anchorlinkRef.current;

    // Add the event listener
    const debouncedHandleScroll = debounce(handleScroll, 100);
    if (element) {
      element.addEventListener('scroll', debouncedHandleScroll);
    }

    // Clean up the event listener when the component is unmounted
    return () => {
      if (element) {
        element.removeEventListener('scroll', debouncedHandleScroll);
      }
    };
  }, [handleScroll]);

  useEffect(() => {
    onHeightChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowMore]);

  const getGallery = (isInPageNavigation: boolean): PostGallery[] => {
    const { postGallery = [], contentAnchorGallery = [] } = postingData;
    return isInPageNavigation ? contentAnchorGallery : postGallery;
  };

  let adNumber = 0;
  let firstAdInjected = false;
  let adUnit = 0;
  let paragraphOffset = 0;
  let adRendered = false;
  const contentRef = useRef(null);
  const contentAnchorRef: Record<string, HTMLDivElement> = {};
  const heroImageRef = useRef(null);
  let paragraphCount: number[] = [];
  let parsedImageCount = 0;
  const articleTypeInGA = getArticleTypeInGA(
    postingData.postCategory,
    postingData.videoType,
  );

  useEffect(() => {
    const currentHeight = contentRef.current
      ? contentRef.current.scrollHeight
      : 0;
    handleShowInformation(currentHeight);
    const imageHeight = heroImageRef.current
      ? heroImageRef.current.clientHeight
      : 0;
    const navSectionHeight = map(contentAnchorRef, (item) => item.clientHeight);
    setInPageNavSectionHeight(navSectionHeight);
    setHeroImageHeight(imageHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // @ts-ignore
  memo((props, state) => {
    if (
      state.visible ||
      state.isShowMore !== isShowMore ||
      state.inPageNavSectionHeight !== inPageNavSectionHeight
    ) {
      return true;
    }
    return !adRendered;
  });

  const handleShowInformation = (currentHeight: number) => {
    if (morePostLoaded) return;
    setIsShowMore(currentHeight > MAX_CONTENT_HEIGHT);
  };

  const showMoreInfo = () => {
    setIsShowMore(false);
    setMorePostLoaded(false);
  };

  const renderSharpieImage = (
    url: string,
    imgAlt: string,
    lazyLoad = true,
    width?: number,
    height?: number,
    option = 'crop',
  ) => {
    const imageInfo = imageConfig[deviceType];
    const sharpieImgUri = handleImageUrl(
      url,
      imageInfo.width,
      imageInfo.height,
      isNextGenSupported,
      option,
    );
    return (
      <LazyImage
        src={sharpieImgUri}
        alt={imgAlt}
        lazyLoad={lazyLoad}
        // @ts-ignore
        width={width}
        // @ts-ignore
        height={height}
      />
    );
  };

  const renderContentCarouselItems = (
    idGalleries: string[],
    isInPageNavigation: boolean,
  ) => {
    const gallery = getGallery(isInPageNavigation);

    return map(idGalleries, (item, i) => {
      const galleryDetailItem = gallery.find((image) => image.ID === item);
      const galleryUrl =
        galleryDetailItem &&
        handleImageUrl(
          galleryDetailItem.url,
          imageInfo.width,
          imageInfo.height,
          isNextGenSupported,
          'fit',
        );
      return (
        <ItemWrapper key={i}>
          {!isEmpty(galleryDetailItem) && (
            <Image>
              <LazyImage src={galleryUrl} alt={galleryDetailItem.title} />
            </Image>
          )}
        </ItemWrapper>
      );
    });
  };

  const shouldInjectAd = (element: string): boolean => {
    if (
      (PARAGRAPH_REGULAR.test(element) ||
        PARAGRAPH_WITH_ATTR_REGULAR.test(element)) &&
      ((isMobile(deviceType) && adNumber < MAX_MOBILE_AD_NUMBER) ||
        (isTablet(deviceType) && adNumber < MAX_TABLET_AD_NUMBER))
    ) {
      paragraphOffset++;

      if (
        (firstAdInjected && paragraphOffset === REST_AD_PARAGRAPH_INTERVAL) ||
        (!firstAdInjected && paragraphOffset === FIRST_AD_PARAGRAPH_INTERVAL)
      ) {
        paragraphOffset = 0;
        firstAdInjected = true;
        adNumber++;

        adUnit++;
        adUnit = adUnit > 3 ? 1 : adUnit;
        return true;
      }

      return false;
    }

    return false;
  };

  const renderContentCarouselMobileItems = (
    idGalleries: string[],
    isInPageNavigation: boolean,
  ): React.ReactNode => {
    const gallery = getGallery(isInPageNavigation);

    return map(idGalleries, (item, i) => {
      const galleryDetailItem = gallery.find((image) => image.ID === item);

      return (
        <ItemWrapper key={i}>
          <DotBackground />
          {galleryDetailItem && (
            <Image>
              {renderSharpieImage(
                galleryDetailItem.url,
                galleryDetailItem.alt_text,
                true,
                undefined,
                undefined,
                'fit',
              )}
            </Image>
          )}
        </ItemWrapper>
      );
    });
  };

  const getAdUnit = (adUnitNum: number): string => {
    if (isMobile(deviceType) || isTablet(deviceType)) {
      return `${AdUnits.AP_Medrec}${adUnitNum}`;
    } else {
      return AdUnits.AP_Medrec;
    }
  };

  const addOpenLinksInNewTab = (htmlContent: string): string => {
    const modifiedHtmlContent = htmlContent.replace(
      /<a([^>]*)>/g,
      (match, attributes) => {
        if (
          attributes.includes('data-newtab="false"') ||
          attributes.includes('target="_blank"')
        ) {
          return match;
        }
        const newAttributes = attributes.trim() ? `${attributes} ` : '';
        return `<a ${newAttributes}target="_blank" rel="noopener">`;
      },
    );

    return modifiedHtmlContent;
  };

  const renderPostContent = (content: string): React.ReactNode => {
    const isBrowser = !!(process.browser || process.env.browser);
    adRendered = isBrowser;
    const { id, project } = postingData;
    const matches = splitContentByParagraph(content);

    return map(matches, (match, i) => {
      const shouldInjectAdItem = shouldInjectAd('<p>' + match);
      const modifiedMatch = addOpenLinksInNewTab(match);

      return (
        <div key={i}>
          {renderContentParagraph(modifiedMatch)}
          {shouldInjectAdItem && isMobile(deviceType) && (
            <CardsAds
              adUnit={getAdUnit(adUnit)}
              sect={isString(sect) ? [sect] : sect}
              subSect={subSect}
              project={project}
              articleId={id}
            />
          )}
        </div>
      );
    });
  };

  const renderContentParagraph = (content: string): React.ReactNode => {
    content = formatContentImages(content);
    content = addOpenLinksInNewTab(content);

    // Set the default post content
    let wrapperElement = (
      <PostContentWrapper
        // @ts-ignore
        isReviewArticle={postCategory === articlePostCategory.review}
        dangerouslySetInnerHTML={{ __html: '<p>' + content }}
      />
    );

    // If paragraph contains image, set it to lazy loading (except for first image in article)
    if (content.includes('<img ')) {
      const isLazyLoad = parsedImageCount > 1;

      // Set lazy loading using js (all browsers support)
      // This will be removed once more browser support is available for native lazy load
      // Important: Currently this will not work if there are inline text in image or image is wrapped in anchor
      if (
        content.match(/<img/g).length === 1 &&
        content.match(ELIGIBLE_LAZY_LOAD_IMAGE)
      ) {
        const parseImages = ELIGIBLE_LAZY_LOAD_IMAGE.exec(content);
        if (parseImages && parseImages[1]) {
          wrapperElement = (
            <PostContentWrapper
              // @ts-ignore
              postCategory={postCategory === articlePostCategory.review}
            >
              <div className={isLazyLoad ? 'lazy-test' : undefined}>
                <LazyImage
                  src={parseImages[1]}
                  alt={parseImages[2]}
                  width={parseImages[3]}
                  height={parseImages[4]}
                  lazyLoad={isLazyLoad}
                />
                {parseImages[5] && (
                  <span className="figcaption">{parseImages[5]}</span>
                )}
              </div>
            </PostContentWrapper>
          );
        }
      }
    }

    return wrapperElement;
  };

  const formatContentImages = (content: string): string => {
    // Extract images
    const imgHtmlTags = getTextBetween(content, '<img', '/>');
    imgHtmlTags.forEach((imgHtmlTag) => {
      const isShowHeroImage =
        postCategory === articlePostCategory.review && articleHeroImage?.url;
      const isFirstImage = parsedImageCount === 0 && !isShowHeroImage;
      parsedImageCount++;

      // Replace sharpie images
      let parsedImgHtmlTag = convertToSharpieImage(imgHtmlTag, isFirstImage);

      // Set browser based lazy load
      if (!isFirstImage) {
        parsedImgHtmlTag = convertToBrowserLazyLoad(parsedImgHtmlTag);
      }

      // Replace the html image tag within the content with the newly parsed one
      content = content.replace(imgHtmlTag, parsedImgHtmlTag);
    });

    return content;
  };

  const convertToSharpieImage = (imgHtml: string, isFirstImage: boolean) => {
    let result = imgHtml;

    // Extract each image url and replace with sharpie's
    const imgParts = /<img.*?src="(.*?)"[^>]+>/g.exec(imgHtml);
    if (imgParts && imgParts[1]) {
      const url = imgParts[1];

      const sharpieImgUri = handleImageUrl(
        url,
        isFirstImage
          ? contentHeroImageConfig[deviceType].width
          : imageInfo.width,
        isFirstImage
          ? contentHeroImageConfig[deviceType].height
          : imageInfo.height,
        isNextGenSupported,
        'fit',
      );

      if (sharpieImgUri !== url) {
        let sharpieImgTag = imgHtml.replace(
          new RegExp(url, 'g'),
          sharpieImgUri,
        );
        const attrSrcset = sharpieImgTag.match(/srcset="(.*?sizes=".*?)"/g);
        sharpieImgTag = attrSrcset
          ? sharpieImgTag.replace(attrSrcset[0], '')
          : sharpieImgTag;

        result = sharpieImgTag;
      }
    }

    return result;
  };

  const convertToBrowserLazyLoad = (imgHtml: string) => {
    return imgHtml.replace('<img ', '<img loading="lazy" ');
  };

  const getCaptions = (
    idGalleries: string[],
    isInPageNavigation: boolean,
  ): string[] => {
    const gallery = getGallery(isInPageNavigation);
    return map(idGalleries, (item) => {
      const galleryDetailItem = gallery.find((image) => image.ID === item);
      if (galleryDetailItem?.caption) {
        return galleryDetailItem.caption;
      } else if (galleryDetailItem?.description) {
        return galleryDetailItem?.description;
      } else {
        return galleryDetailItem?.title || '';
      }
    });
  };

  const renderPostContentImageGallery = (
    idGalleries: string[],
    i: number,
    isInPageNavigation?: boolean,
  ) => {
    return (
      <>
        <MobileCarousel
          postingData={postingData}
          totalItem={idGalleries.length}
          source={ADP.GALLERY}
          captions={getCaptions(idGalleries, isInPageNavigation)}
        >
          {renderContentCarouselMobileItems(idGalleries, isInPageNavigation)}
        </MobileCarousel>
        <ContentCarousel
          postingData={postingData}
          idGalleries={idGalleries}
          totalItem={idGalleries.length}
          source={ADP.GALLERY}
          lazyLoad
          captions={getCaptions(idGalleries, isInPageNavigation)}
          gallery={getGallery(isInPageNavigation)}
        >
          {renderContentCarouselItems(idGalleries, isInPageNavigation)}
        </ContentCarousel>
      </>
    );
  };

  const renderInPageNavSectionDescription = (
    matches: string[],
    sectionIndex: number,
  ) => {
    const { id, project } = postingData;
    const numberOfChunk = paragraphCount.length - 1;
    const previousParagraphCount = paragraphCount.reduce((a, b, i) => {
      if (i <= numberOfChunk - 2) {
        return a + b;
      }
      return a;
    }, 0);

    return map(matches, (match, i) => {
      return (
        <div key={i}>
          {renderContentParagraph(match)}
          {/* In Page Navigation Ads Logic */}
          {
            // Put ad at 4th and 6th paragraph of Section 2
            sectionIndex === 1 &&
              (i +
                paragraphCount[numberOfChunk - 1] +
                previousParagraphCount ===
                4 ||
                i +
                  paragraphCount[numberOfChunk - 1] +
                  previousParagraphCount ===
                  6) &&
              isMobile(deviceType) && (
                <CardsAds
                  adUnit={getAdUnit(++adUnit)}
                  sect={isString(sect) ? [sect] : sect}
                  project={project}
                  articleId={id}
                />
              )
          }
        </div>
      );
    });
  };

  const renderInPageNavigationPostContent = (
    contentAnchor: Post['contentAnchor'],
  ) => {
    const { id, project } = postingData;
    return (
      <div data-test={'in-page-navigation-post-content'}>
        {map(contentAnchor, (item, i) => {
          const sectionTitle = !isEmpty(item.description) ? item.title : null;
          const showSplitContentDescription = split(item.description, '^|^');
          paragraphCount = [0];
          return (
            <div
              key={i}
              ref={(node) => {
                contentAnchorRef[`section-${i}`] = node;
              }}
            >
              <h2 id={item.title + '_section'}>
                {anchorContentTitle[sectionTitle]}
              </h2>
              {map(showSplitContentDescription, (splitItem, itemIndex) => {
                let finalJsx: React.ReactNode;
                if (splitItem.indexOf('[^POST_GALLERY^]') === 0) {
                  const idGalleries = split(
                    splitItem.replace(/[^0-9,]/g, ''),
                    ',',
                  );
                  finalJsx = renderPostContentImageGallery(
                    idGalleries,
                    itemIndex,
                    true,
                  );
                } else {
                  const matches = splitContentByParagraph(splitItem);
                  paragraphCount.push(matches.length - 1);
                  finalJsx = renderInPageNavSectionDescription(matches, i);
                }
                return (
                  <div key={itemIndex}>
                    {finalJsx}
                    {/* In Page Navigation Ads Logic */}
                    {
                      // Put ad at the end of Section 1
                      i === 0 &&
                        showSplitContentDescription.length === itemIndex + 1 &&
                        isMobile(deviceType) &&
                        contentAnchor.length > 1 && (
                          <CardsAds
                            adUnit={getAdUnit(++adUnit)}
                            sect={isString(sect) ? [sect] : sect}
                            project={project}
                            articleId={id}
                          />
                        )
                    }
                  </div>
                );
              })}
              {showLeadCaptureForm &&
                ['price_condition']?.includes(sectionTitle) && (
                  <FormContent>
                    <LeadsCapture
                      projectId={postingData?.project?.id}
                      leadCaptureFormId={
                        postingData?.project?.leadCaptureFormId
                      }
                    />
                  </FormContent>
                )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleNavLinkScroll = (
    direction: 'left' | 'right',
    scrollAmount: number,
  ) => {
    if (direction === 'right') {
      anchorlinkRef.current.scrollLeft += scrollAmount;
    } else {
      anchorlinkRef.current.scrollLeft -= scrollAmount;
    }
  };

  const handleLinkArrowClick = (
    direction: 'left' | 'right',
    scrollAmount: number,
  ) => {
    handleNavLinkScroll(direction, scrollAmount);
  };

  //function to render anchor link contents.
  function renderAnchorLinkContents() {
    const { Link } = Anchor;

    return map(contentAnchor, (item, i) => {
      const anchorMenu = !isEmpty(item.description) ? item.title : null;
      const anchorLink = !isEmpty(anchorMenu) ? `#${item.title}_section` : '';
      return (
        !isEmpty(item.description) && (
          <Link
            href={anchorLink}
            title={anchorContentTitle[anchorMenu]}
            key={i}
            // @ts-ignore
            prefetch={false}
          />
        )
      );
    });
  }
  const renderContentAnchor = (contentAnchor: ContentAnchor[]) => {
    const { Link } = Anchor;

    const onAnchorClick = (
      e: React.MouseEvent<HTMLElement>,
      link: {
        title: string;
        href: string;
      },
    ) => {
      const anchorIndex = findIndex(contentAnchor, ({ title }) =>
        link.href.includes(title),
      );
      const totalSectionHeight = inPageNavSectionHeight.reduce(
        (accumulator, currentValue, index) => {
          if (index <= anchorIndex) {
            return accumulator + currentValue;
          }
          return accumulator;
        },
      );
      e.preventDefault();
      if (totalSectionHeight + heroImageHeight > MAX_CONTENT_HEIGHT) {
        setIsShowMore(false);
      }
      trackInPageNavigation(
        userInfo,
        { ...postingData, articleTypeInGA },
        ADP.PAGE,
        anchorContentTitleEnglish[link.title],
      );
    };

    const onAnchorChange = () => {
      const restoreScroll = debounce((event: Event) => {
        window.removeEventListener('scroll', preventScroll, true);
        event.target.dispatchEvent(event);
      }, 50);

      const preventScroll = (event: Event) => {
        event.stopPropagation();
        restoreScroll(event);
      };
      window.addEventListener('scroll', preventScroll, true);
    };

    return (
      <Anchor
        targetOffset={87}
        // @ts-ignore
        offsetBottom={100}
        onClick={onAnchorClick}
        onChange={onAnchorChange}
      >
        <div className="scrollable-tabs-container">
          <div className="scroll_wrapper" ref={anchorlinkRef}>
            <div className={`left-arrow ${isLeftArrowActive ? 'active' : ''}`}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                onClick={() => handleLinkArrowClick('left', 200)}
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.75 19.5L8.25 12l7.5-7.5"
                />
              </svg>
            </div>
            {renderAnchorLinkContents()}

            {showLeadCaptureForm && (
              <Link
                className="cta-anchor"
                title={anchorContentTitle['lead_form_nav_btn']}
                href="#leads_form_section"
              />
            )}
            <div
              className={`right-arrow ${isRightArrowActive ? 'active' : ''}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                onClick={() => {
                  handleLinkArrowClick('right', 200);
                }}
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M8.25 4.5l7.5 7.5-7.5 7.5"
                />
              </svg>
            </div>
          </div>
        </div>
      </Anchor>
    );
  };

  const {
    content,
    articleHeroImage,
    contentAnchor,
    postCategory,
    latestReviewPost,
  } = postingData;
  const articleBody =
    postCategory === articlePostCategory.review
      ? content + (!isEmpty(contentAnchor) ? '^|^[^IN_PAGE_NAV^]^|^' : '')
      : content;
  const showSplitContentBody = split(articleBody, '^|^');
  const hasAnchorContent = !isEmpty(
    filter(
      contentAnchor,
      (item) => item.title !== '' && item.description !== '',
    ),
  );

  const postContentContainer = (
    <PostContentContainer className="post-content" data-test={'post-content'}>
      <article data-clarity-region="article">
        <PostArticle
          ref={contentRef}
          // @ts-ignore
          isShowMore={isShowMore}
          maxHeight={MAX_CONTENT_HEIGHT}
          hasAnchor={hasAnchorContent}
        >
          {postCategory === articlePostCategory.review &&
            postingData.videoType == VideoType.None &&
            !isEmpty(articleHeroImage.url) && (
              <ContentHeroImageWrapper
                ref={heroImageRef}
                data-test={'content-hero-image'}
              >
                <LazyImage
                  src={handleImageUrl(
                    articleHeroImage?.url ?? '',
                    contentHeroImageConfig[deviceType]?.width,
                    contentHeroImageConfig[deviceType]?.height,
                    isNextGenSupported,
                    'crop',
                  )}
                  alt=""
                  lazyLoad={false}
                  // @ts-ignore
                  width={articleHeroImage.width}
                  // @ts-ignore
                  height={articleHeroImage.height}
                />
              </ContentHeroImageWrapper>
            )}
          <LatestProjectCard {...latestReviewPost} />
          {postCategory === articlePostCategory.review &&
            hasAnchorContent &&
            renderContentAnchor(contentAnchor)}

          <div data-test={'split-content-section'}>
            {map(showSplitContentBody, (item, i) => {
              let finalContent: React.ReactNode;
              if (item.indexOf('[^POST_GALLERY^]') === 0) {
                const idGalleries = split(item.replace(/[^0-9,]/g, ''), ',');
                finalContent = renderPostContentImageGallery(idGalleries, i);
              } else if (
                !isEmpty(contentAnchor) &&
                item.indexOf('[^IN_PAGE_NAV^]') === 0
              ) {
                finalContent = renderInPageNavigationPostContent(contentAnchor);
              } else {
                const modifiedContent = addOpenLinksInNewTab(item);
                finalContent = (
                  <>
                    {renderPostContent(modifiedContent)}
                    {modifiedContent && showLeadCaptureForm && (
                      <FormContent>
                        <LeadsCapture
                          projectId={postingData?.project?.id}
                          leadCaptureFormId={
                            postingData?.project?.leadCaptureFormId
                          }
                        />
                      </FormContent>
                    )}
                  </>
                );
              }

              return (
                <div key={i} data-test={'final-content-item'}>
                  {finalContent}
                </div>
              );
            })}
          </div>
          {/* This hack is to fix a mobile webkit issue in ACO-1010 */}
          <div aria-hidden="true" style={{ opacity: '0', height: 0 }}>
            0
          </div>
        </PostArticle>
      </article>
      {isShowMore && (
        <ShowMoreWrapper>
          <ShowMore
            showMoreInfo={() => showMoreInfo()}
            showMoreText={contentResource.button.continueReading}
            source={ADP.PAGE}
            postingData={postingData}
            marginTop={0}
          />
        </ShowMoreWrapper>
      )}
    </PostContentContainer>
  );

  const endTime = new Date().getTime();
  if (!timeWarningLogged.current && endTime - startTime > 1000) {
    logger.logError(
      'PostContent',
      `Rendering time ${endTime - startTime} is too long for post:${
        postingData.id
      }`,
    );

    timeWarningLogged.current = true;
  } else {
    logger.logInfo(
      'PostContent',
      `Rendering time is ${endTime - startTime} for post:${postingData.id}`,
    );
  }
  return postContentContainer;
};

interface PostContentProps {
  postingData: Post;
  sect: string | string[];
  subSect: string;
  onHeightChange: () => void;
  showLeadCaptureForm: boolean;
}

export default PostContent;
